import { useEffect, useMemo, useRef, useState } from "react";

export interface DropdownSelectorOption {
    key: string;
    label: string;
    isDisabled?: boolean;
}

export interface DropdownSelectorGroup {
    label: string;
    options: DropdownSelectorOption[];
}

export interface DropdownSelectorProps {
    label: string;
    labelAdornment?: React.ReactNode;
    selectedKey: string | null;
    data: DropdownSelectorOption[] | DropdownSelectorGroup[];
    isEditable: boolean;
    onSelectionChange: (key: string) => void;
    enableSearch?: boolean;
    searchPlaceholder?: string;
    emptyText?: string;
    /** Label shown as the current value when `selectedKey` has no matching option (e.g. a removed category). */
    currentValueFallbackLabel?: string;
    /** Label shown as the current value instead of the matching option's label (e.g. a frozen historical name). */
    currentValueLabelOverride?: string;
    /** Renders the control with a red border and marks it invalid. */
    hasError?: boolean;
}

export function DropdownSelector({
    label,
    labelAdornment,
    selectedKey,
    data,
    isEditable,
    onSelectionChange,
    enableSearch = false,
    searchPlaceholder = "Search...",
    emptyText = "No options found",
    currentValueFallbackLabel,
    currentValueLabelOverride,
    hasError = false,
}: DropdownSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const baseIdRef = useRef(`dropdown-selector-${Math.random().toString(36).slice(2, 10)}`);
    const labelId = `${baseIdRef.current}-label`;
    const triggerId = `${baseIdRef.current}-trigger`;
    const listboxId = `${baseIdRef.current}-listbox`;

    const isGroupedData = (value: DropdownSelectorProps["data"]): value is DropdownSelectorGroup[] => {
        return value.length > 0 && "options" in value[0];
    };

    const flatOptions = useMemo(() => (isGroupedData(data) ? data.flatMap((group) => group.options) : data), [data]);

    const selectedOption = useMemo(() => {
        if (!selectedKey) return null;
        return flatOptions.find((option) => option.key === selectedKey) ?? null;
    }, [flatOptions, selectedKey]);

    // Display the option label when found; otherwise fall back to the provided label for an unlisted selected key.
    const currentValueLabel = currentValueLabelOverride ?? selectedOption?.label ?? (selectedKey ? (currentValueFallbackLabel ?? null) : null);

    const borderClass = hasError ? "border-red-500" : "border-gray-300";

    const filteredGroups = useMemo(() => {
        const normalizedSearch = enableSearch ? search.trim().toLowerCase() : "";
        if (!normalizedSearch) return data;

        if (isGroupedData(data)) {
            return data
                .map((group) => ({
                    ...group,
                    options: group.options.filter((option) => option.label.toLowerCase().includes(normalizedSearch)),
                }))
                .filter((group) => group.options.length > 0);
        } else {
            return (data as DropdownSelectorOption[]).filter((option) => option.label.toLowerCase().includes(normalizedSearch));
        }
    }, [data, search, enableSearch]);

    useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (containerRef.current && !containerRef.current.contains(target)) {
                setIsOpen(false);
                setSearch("");
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [isOpen]);

    const handleOpen = () => {
        setIsOpen(true);
        setSearch("");
    };

    const handleClose = () => {
        setIsOpen(false);
        setSearch("");
    };

    return (
        <div ref={containerRef} className="relative flex flex-col gap-1">
            <label id={labelId} className="flex items-center gap-1 text-sm font-medium text-gray-700">
                {label}
                {labelAdornment}
            </label>
            {!isEditable ? (
                <div className={`flex h-11 items-center rounded-lg border ${borderClass} px-3 text-sm text-gray-900`}>{currentValueLabel ?? ""}</div>
            ) : isOpen && enableSearch ? (
                <input
                    id={triggerId}
                    autoFocus
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Escape") handleClose();
                    }}
                    role="combobox"
                    aria-label={label}
                    aria-labelledby={labelId}
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    placeholder={searchPlaceholder}
                    className="h-11 w-full rounded-lg border border-blue-500 px-3 text-sm text-gray-900 outline-none"
                />
            ) : (
                <button
                    id={triggerId}
                    type="button"
                    aria-labelledby={labelId}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-invalid={hasError || undefined}
                    className={`flex h-11 w-full items-center justify-between rounded-lg border ${borderClass} px-3 text-left text-sm`}
                    onClick={handleOpen}
                >
                    <span className={`truncate ${currentValueLabel ? "text-gray-900" : "text-gray-400"}`}>{currentValueLabel ?? "Select"}</span>
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            )}

            {isEditable && isOpen && (
                <div
                    id={listboxId}
                    role="listbox"
                    aria-labelledby={labelId}
                    className="absolute z-20 mt-[76px] max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                >
                    {filteredGroups.length > 0 ? (
                        isGroupedData(filteredGroups) ? (
                            filteredGroups.map((group) => (
                                <div key={group.label}>
                                    <p className="sticky top-0 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-900">{group.label}</p>
                                    {group.options.map((option) => {
                                        const isSelected = option.key === selectedKey;
                                        return (
                                            <button
                                                key={option.key}
                                                type="button"
                                                role="option"
                                                aria-selected={isSelected}
                                                aria-disabled={option.isDisabled ? "true" : "false"}
                                                disabled={option.isDisabled}
                                                className={`block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 ${isSelected ? "bg-blue-50 font-medium text-gray-900" : ""}`}
                                                onClick={() => {
                                                    if (option.isDisabled) return;
                                                    onSelectionChange(option.key);
                                                    handleClose();
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        ) : (
                            filteredGroups.map((option) => {
                                const isSelected = option.key === selectedKey;
                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        aria-disabled={option.isDisabled ? "true" : "false"}
                                        disabled={option.isDisabled}
                                        className={`block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 ${isSelected ? "bg-blue-50 font-medium text-gray-900" : ""}`}
                                        onClick={() => {
                                            if (option.isDisabled) return;
                                            onSelectionChange(option.key);
                                            handleClose();
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })
                        )
                    ) : (
                        <p className="px-3 py-2 text-sm text-gray-500">{emptyText}</p>
                    )}
                </div>
            )}
        </div>
    );
}
